import { useEffect, useState } from 'react';

/**
 * Avisos de rodapé (toasts).
 *
 * Toda ação precisa de resposta visível. Sem isso, a pessoa clica em "salvar",
 * nada pisca, e ela clica de novo — o que gera o registro duplicado que
 * passamos o projeto inteiro evitando.
 *
 * Sem biblioteca e sem contexto do React: um emissor de módulo. Qualquer
 * arquivo chama `avisar(...)` sem precisar receber nada por propriedade.
 */

export type Tom = 'bom' | 'ruim';
type Aviso = { id: number; texto: string; tom: Tom };

let proximo = 1;
const ouvintes = new Set<(a: Aviso) => void>();

export function avisar(texto: string, tom: Tom = 'bom'): void {
  const aviso = { id: proximo++, texto, tom };
  ouvintes.forEach((o) => o(aviso));
}

/** Atalho para o `catch`: mostra a mensagem do erro como veio da API. */
export function avisarErro(e: unknown): void {
  avisar(e instanceof Error ? e.message : 'Algo não funcionou.', 'ruim');
}

const DURACAO = 4200;

export function Avisos() {
  const [lista, setLista] = useState<Aviso[]>([]);

  useEffect(() => {
    const aoChegar = (a: Aviso) => {
      setLista((atual) => [...atual, a]);
      // Erro fica um pouco mais: é o que a pessoa precisa ler com calma.
      setTimeout(
        () => setLista((atual) => atual.filter((x) => x.id !== a.id)),
        a.tom === 'ruim' ? DURACAO * 2 : DURACAO,
      );
    };
    ouvintes.add(aoChegar);
    return () => {
      ouvintes.delete(aoChegar);
    };
  }, []);

  if (lista.length === 0) return null;

  return (
    <div className="avisos" role="status" aria-live="polite">
      {lista.map((a) => (
        <div key={a.id} className={`aviso aviso-${a.tom}`}>
          {a.texto}
          <button
            type="button"
            className="aviso-fechar"
            onClick={() => setLista((atual) => atual.filter((x) => x.id !== a.id))}
            aria-label="Fechar aviso"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
